import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useMutation, useLazyQuery } from 'react-apollo';
import {
  Form, FormGroup, Label, Input, ButtonGroup, FormText, Button,
} from 'reactstrap';
import { useToasts } from 'react-toast-notifications';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import {
  LOGO_LARGE_WHITE, routeConstants, handleGraphqlError,
  validationMessages, regexPattern, staticConstants, NUMERIC_VALUE,
} from '../../../utils';
import RegisterForm from '../register/RegisterForm';
import { LOGIN_MUTATION, LOADER_MUTATION } from '../../../graphql-client/mutations';
import InputError from '../elments/InputError';
import { VERIFY_INVITATION } from '../../../graphql-client/queries';
import Loading from '../loadingOverlay/Loading';
import { firebaseAuth } from '../../../config/firebase';

export default function AuthForm(props) {
  const { register, handleSubmit, errors } = useForm({ mode: 'onBlur', nativeValidation: false });
  const { location: { state, hash } } = props;
  const history = useHistory();
  const { addToast } = useToasts();
  const [signUpModal, setSignUpModal] = useState(false);
  const [apiData, setApiData] = useState();
  const [invitationToken, setInvitationtoken] = useState(false);

  const [mutate] = useMutation(LOADER_MUTATION, {
    variables: { show: false },
  });
  const [verifyInvitation, { loading: verifyInvitationLoading, data: verifyInvitationData }] = useLazyQuery(VERIFY_INVITATION, {
    variables: { token: invitationToken },
    onError: (verifyInvitationError) => {
      addToast(handleGraphqlError(verifyInvitationError), { appearance: 'error' });
    },
  });
  const [login] = useMutation(LOGIN_MUTATION, {
    variables: apiData,

    onError(error) {
      mutate({ variables: { show: false } });
      addToast(handleGraphqlError(error), { appearance: 'error' });
    },

    onCompleted: async (data) => {
      mutate({ variables: { show: false } });
      if (data) {
        const user = data.login.data;
        const { token } = user;
        try {
          await firebaseAuth.signInWithEmailAndPassword(apiData.email, apiData.password);
        } catch (error) {
          if (error.code === 'auth/user-not-found') {
            try {
              await firebaseAuth.createUserWithEmailAndPassword(apiData.email, apiData.password);
            } catch (erro) {
              addToast(erro.message, { appearance: 'error' });
              return;
            }
          }
        }
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);
        const pathToRedirect = user.role === staticConstants.ROLE.SYSTEM_ADMIN ? routeConstants.ADMIN_OFFER : routeConstants.DASHBOARD;
        const path = user.isFirstTime ? routeConstants.CREATE_PASSWORD : pathToRedirect;
        if (state && state.referrer) {
          if (path === routeConstants.CREATE_PASSWORD) {
            history.push({
              pathname: path,
              state: { referrer: state.referrer, inviteOfferId: user.inviteOfferId, userId: user._id },
            });
          } else if (state.referrer.split('/').length === 5) {
            history.push(state.referrer);
          } else {
            const splitVal = state.referrer.split('/');
            const newPath = `/dashboard/${user._id}/${splitVal[splitVal.length - 1]}`;
            history.push(newPath);
          }
          return;
        }
        history.push({ pathname: path, state: { inviteOfferId: user.inviteOfferId, userId: user._id } });
      }
    },
  });

  useEffect(() => {
    if (invitationToken) {
      verifyInvitation(invitationToken);
    }
  }, [invitationToken]);

  useEffect(() => {
    if (verifyInvitationData && verifyInvitationData.verifyInvitation) {
      setSignUpModal(true);
    }
    if (verifyInvitationData && !verifyInvitationData.verifyInvitation) {
      addToast('Invalid verification token', { appearance: 'error' });
    }
  }, [verifyInvitationData]);
  useEffect(() => {
    if (props && props.match && props.match.params && props.match.params.token) {
      setInvitationtoken(props.match.params.token);
    }
    if (hash && ['#register', '#artistRegister', '#managerRegister'].indexOf(hash) > -1) {
      setSignUpModal(true);
    }
  }, [props]);

  if (verifyInvitationLoading) {
    return <Loading active />;
  }

  const onSubmit = (data) => {
    const { username, userpassword } = data;
    setApiData({ email: username, password: userpassword });
    mutate({ variables: { show: true } });
    login({ username, userpassword });
  };
  return (
    <>
      <div className="login-wrapper">
        <div className="form-container">
          <div className="login-logo">
            <img src={LOGO_LARGE_WHITE} alt="BYGmusic" />
          </div>
          <p>
            A place for artists to earn money working with amazing brands through sponsorships, content creation, social media, and live streams
            {' '}
          </p>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup className={errors.username ? 'form-error' : ''}>
              <Label
                for="Email"
                className="white-label"
              >
                Email address
              </Label>
              <Input
                type="text"
                name="username"
                innerRef={register({
                  required: true,
                  type: 'email',
                  pattern: { value: regexPattern.EMAIL, message: validationMessages.EMAIL },
                })}
                placeholder="Enter email address"
              />
              <InputError error={errors.username} max={NUMERIC_VALUE.TWO_HUNDRED} min={NUMERIC_VALUE.THREE} />
            </FormGroup>
            <FormGroup className={errors.userpassword ? 'form-error' : ''}>
              <Label
                for="Password"
                className="white-label"
              >
                Password
              </Label>
              <Input
                innerRef={register({ required: true, maxLength: NUMERIC_VALUE.TWO_HUNDRED, minLength: NUMERIC_VALUE.THREE })}
                type="password"
                name="userpassword"
                placeholder="Enter password"
              />
              <InputError error={errors.userpassword} />
              <Link to={`${routeConstants.FORGOT_PASSWORD}`}>Forgot?</Link>
            </FormGroup>
            <ButtonGroup>
              <Button
                color="primary"
                size="lg"
                element="block"
                type="submit"
              >
                Sign in
              </Button>
            </ButtonGroup>
            <FormText
              color="fade"
            >
              Don’t have an account yet?
              <span onClick={() => setSignUpModal(true)} onKeyDown={() => setSignUpModal(true)}> Sign up</span>
            </FormText>
          </Form>
        </div>
      </div>
      { signUpModal && (
        <RegisterForm
          invitationData={verifyInvitationData && verifyInvitationData.verifyInvitation.data && { ...verifyInvitationData.verifyInvitation.data, ...{ invitationToken } }}
          isOpen={signUpModal}
          onClose={() => { setSignUpModal(false); }}
          hash={hash}
        />
      )}
    </>
  );
}
AuthForm.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    state: PropTypes.object,
    hash: PropTypes.string,
  }),

  match: PropTypes.shape({ params: PropTypes.shape({ token: PropTypes.string }) }),
};

AuthForm.defaultProps = {
  location: null,
  match: null,
};
